import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2 } from "lucide-react";

interface Hairstyle {
  id: string;
  name: string;
  image_url: string;
  category: string | null;
  ethnicity: string | null;
}

interface HairstyleCarouselProps {
  onSelect: (hairstyle: Hairstyle) => void;
  onApply: () => void;
  selectedId?: string;
  userEthnicity?: string | null;
  hasUserPhoto: boolean;
}

const LazyHairstyleImage = ({ 
  style, 
  selectedId, 
  hasUserPhoto, 
  onSelect, 
  onApply 
}: { 
  style: Hairstyle; 
  selectedId?: string; 
  hasUserPhoto: boolean;
  onSelect: (style: Hairstyle) => void;
  onApply: () => void;
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef<HTMLDivElement>(null);

  // Create thumbnail URL using Supabase image transformation
  const getThumbnailUrl = (url: string) => {
    try {
      const urlObj = new URL(url);
      // Check if it's a Supabase storage URL
      if (urlObj.hostname.includes('supabase')) {
        // Convert to render/image endpoint with transformation
        const path = urlObj.pathname.replace('/storage/v1/object/public/', '');
        const baseUrl = `${urlObj.protocol}//${urlObj.hostname}`;
        return `${baseUrl}/storage/v1/render/image/public/${path}?width=112&height=112&quality=70`;
      }
    } catch (e) {
      console.error('Error creating thumbnail URL:', e);
    }
    return url;
  };

  const thumbnailUrl = getThumbnailUrl(style.image_url);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          // Once loaded, disconnect to free memory
          observer.disconnect();
        }
      },
      { rootMargin: '50px' }
    );

    const currentRef = imgRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <Card
      ref={imgRef}
      onClick={() => onSelect(style)}
      className={`flex-shrink-0 cursor-pointer transition-all duration-200 hover:scale-105 active:scale-95 relative ${
        selectedId === style.id 
          ? 'ring-2 ring-primary ring-offset-2 shadow-lg scale-105' 
          : 'hover:shadow-md'
      }`}
    >
      <div className="w-28 h-28 overflow-hidden rounded-t-lg relative bg-muted">
        {!isLoaded && <Skeleton className="w-full h-full absolute inset-0" />}
        {isInView && (
          <img
            src={thumbnailUrl}
            alt={style.name}
            className={`w-full h-full object-cover transition-opacity duration-300 ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            loading="lazy"
            onLoad={() => setIsLoaded(true)}
            width="112"
            height="112"
          />
        )}
        {selectedId === style.id && hasUserPhoto && (
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/60 to-transparent flex items-center justify-center backdrop-blur-[1px]">
            <Button
              onClick={(e) => {
                e.stopPropagation();
                onApply();
              }}
              size="sm"
              className="text-xs font-semibold px-4 py-2 h-auto shadow-lg hover:scale-105 transition-transform"
            >
              Try Now
            </Button>
          </div>
        )}
        {selectedId === style.id && (
          <div className="absolute top-2 right-2 w-3 h-3 rounded-full bg-primary animate-pulse shadow-lg" />
        )}
      </div>
      <div className="w-28 p-2 text-center bg-card/95 backdrop-blur-sm">
        <p className="text-xs font-medium line-clamp-2 leading-tight break-words text-foreground/90">
          {style.name}
        </p>
      </div>
    </Card>
  );
};

export function HairstyleCarousel({ onSelect, onApply, selectedId, userEthnicity, hasUserPhoto }: HairstyleCarouselProps) {
  const [hairstyles, setHairstyles] = useState<Hairstyle[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const ITEMS_PER_PAGE = 15;

  useEffect(() => {
    // Small delay to let auth settle and prevent memory overload
    const timer = setTimeout(() => {
      fetchHairstyles(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, [userEthnicity]);

  const fetchHairstyles = async (isInitial = false) => {
    if (isInitial) {
      setLoading(true);
      setPage(0);
      setHairstyles([]);
    } else {
      setLoadingMore(true);
    }

    try {
      const from = isInitial ? 0 : (page + 1) * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;

      let query = supabase
        .from('hairstyles')
        .select('*', { count: 'exact' })
        .range(from, to);

      // If user has an ethnicity preference, try to filter by it
      if (userEthnicity) {
        query = query.eq('ethnicity', userEthnicity);
      }
      
      query = query.order('created_at', { ascending: true });

      const { data, error, count } = await query;

      if (error) throw error;
      
      // If ethnicity filter returns no results on first load, fetch all hairstyles
      if (userEthnicity && isInitial && (!data || data.length === 0)) {
        console.log(`No hairstyles found for ethnicity "${userEthnicity}", showing all hairstyles`);
        const fallbackQuery = await supabase
          .from('hairstyles')
          .select('*', { count: 'exact' })
          .range(0, ITEMS_PER_PAGE - 1)
          .order('created_at', { ascending: true });
        
        setHairstyles(fallbackQuery.data || []);
        setHasMore((fallbackQuery.data?.length || 0) >= ITEMS_PER_PAGE);
        setPage(0);
      } else {
        if (isInitial) {
          setHairstyles(data || []);
          setPage(0);
        } else {
          setHairstyles(prev => [...prev, ...(data || [])]);
          setPage(prev => prev + 1);
        }
        setHasMore((data?.length || 0) >= ITEMS_PER_PAGE);
      }
    } catch (error) {
      console.error('Error fetching hairstyles:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-background to-muted/30 border-t">
      <ScrollArea className="w-full">
        <div className="flex gap-3 px-4 py-4">
          {hairstyles.map((style) => (
            <LazyHairstyleImage
              key={style.id}
              style={style}
              selectedId={selectedId}
              hasUserPhoto={hasUserPhoto}
              onSelect={onSelect}
              onApply={onApply}
            />
          ))}
          {hasMore && (
            <div className="flex-shrink-0 w-28 h-36 flex items-center justify-center">
              <Button
                onClick={() => fetchHairstyles(false)}
                disabled={loadingMore}
                variant="outline"
                size="sm"
                className="h-28 w-28 flex flex-col gap-2"
              >
                {loadingMore ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  <>
                    <span className="text-2xl">+</span>
                    <span className="text-xs">Load More</span>
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
        <ScrollBar orientation="horizontal" className="h-2" />
      </ScrollArea>
    </div>
  );
}
